/**
 * Runs tab — a timeline of morning/EOD routine runs (from `useRuns(50)`) with a
 * click-through detail panel (from `useRun(id)`) rendering the run artifact.
 *
 * ARTIFACT CONTRACT (authored against the VERIFIED agent shapes, not guesses):
 *
 *   Runs-index row (artifacts.py `write_run_index`, ~line 120):
 *     { run_id, routine, org_id, env, status, started_at, completed_at,
 *       artifact_uri, summary?, shadow? }
 *   `status` ∈ success | partial | failed (tolerate unknown values — neutral pill).
 *   `summary` carries cheap display fields (account_id, cash, equity,
 *     positions_count, execution_mode, pnl_amount, pnl_pct, …).
 *
 *   Run artifact (the S3 run.json — getRun returns it verbatim as `artifact`):
 *     morning.py (~line 596): { inputs, status, account, positions,
 *                               market_context, summary, completed_at }
 *     eod.py (~line 589) adds: { pnl, snapshot_uri, research }
 *     shadow_record.py (line 152) MERGES IN: artifact["shadow_intended"] = block
 *
 *   The shadow_intended block (shadow.py `record` / `record_abort`, ~line 279):
 *     { schema_version, generated_at, execution_mode, submitted,
 *       plan, validation, intake, limits, provenance }
 *     - plan  (TradePlan.model_dump): { trades:[{trade_id, action, instrument,
 *         symbol, underlying, quantity, strategy, contract_symbol}], summary }.
 *         The agent's decision NARRATIVE is `plan.summary` (free prose).
 *     - validation (ValidationResult.model_dump): { dispositions:[{trade_id,
 *         status:"accept"|"reject", violations:[{code, scope, limit_name,
 *         observed, allowed, message, trade_id}]}], accepted_count,
 *         rejected_count }. The validator's ReasonCode is `violation.code`; the
 *         plain-language line the artifact carries is `violation.message`.
 *     - intake: { status:"accept"|"abort", attempts, reason } — `reason` is the
 *         no-trade narrative on an abort.
 *
 *   THE TLDR is NOT persisted in the artifact — it is assembled at
 *   Telegram-send time by shadow.py `telegram_line` (the headline + bulleted
 *   "TLDR:" block). The console reconstructs an equivalent TLDR client-side from
 *   the same source fields (intake status, plan trades, validation dispositions)
 *   via `runTldr()` below, and surfaces the persisted narrative (`plan.summary`
 *   / `intake.reason`) separately.
 *
 *   There is NO top-level `report` / `narrative` / `executions` field — shadow
 *   mode SUBMITS NOTHING, so there are no executions; the "executions" surface
 *   here lists the accepted dispositions that WOULD have been placed in live
 *   mode, plus any escalations the artifact records.
 *
 * Tolerant rendering throughout: a missing artifact renders the index row only
 * plus a note; missing sections are skipped silently; any unrecognized shape
 * falls through to a raw-JSON <details>. NOTHING throws on a weird artifact.
 *
 * Styling is deferred to Task 8; only semantic classNames are emitted here.
 */

import { useState } from 'react';
import { useRuns, useRun, type RunIndexRow } from '../lib/queries';

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit tests — no DOM needed).
// ---------------------------------------------------------------------------

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

/** Map a raw status to a pill class + label. Unknown values get a neutral pill
 *  rather than throwing or rendering nothing. */
export function statusPill(status: unknown): { className: string; label: string } {
  const s = typeof status === 'string' ? status.toLowerCase() : '';
  switch (s) {
    case 'success':
      return { className: 'pill pill-success', label: 'success' };
    case 'partial':
      return { className: 'pill pill-partial', label: 'partial' };
    case 'failed':
    case 'error':
      return { className: 'pill pill-failed', label: s };
    default:
      return { className: 'pill pill-neutral', label: typeof status === 'string' && status ? status : 'unknown' };
  }
}

/** Best-effort timestamp for ordering/labeling a row: started_at then
 *  completed_at then nothing. */
export function rowTimestamps(row: RunIndexRow): { started: string | null; completed: string | null } {
  return {
    started: asString(row.started_at),
    completed: asString(row.completed_at),
  };
}

export interface RunSummaryCounts {
  positions: number | null;
  accepted: number | null;
  rejected: number | null;
  pnlAmount: number | null;
  pnlPct: number | null;
}

/** Pull the cheap summary counts off an index row's `summary` (or a top-level
 *  fallback). Everything is optional and missing fields read back as null. */
export function summaryCounts(row: RunIndexRow): RunSummaryCounts {
  const summary = asRecord(row.summary) ?? {};
  const shadow = asRecord(row.shadow) ?? {};
  const num = (v: unknown): number | null => (typeof v === 'number' && !Number.isNaN(v) ? v : null);
  return {
    positions: num(summary.positions_count),
    accepted: num(shadow.accepted_count) ?? num(shadow.accepted),
    rejected: num(shadow.rejected_count) ?? num(shadow.rejected),
    pnlAmount: num(summary.pnl_amount),
    pnlPct: num(summary.pnl_pct),
  };
}

/** The shadow_intended block off an artifact, or null. */
export function shadowBlock(artifact: unknown): Record<string, unknown> | null {
  const a = asRecord(artifact);
  return a ? asRecord(a.shadow_intended) : null;
}

export interface ExtractedTrade {
  tradeId: string | null;
  desc: string;
  quantity: number | null;
  status: string | null;
  /** ReasonCode strings shown as-is. */
  codes: string[];
  /** The plain-language validator message(s), when the artifact carries them. */
  messages: string[];
}

function tradeDesc(t: Record<string, unknown>): string {
  const parts = [t.action, t.strategy ?? t.instrument, t.underlying ?? t.symbol]
    .map((p) => (typeof p === 'string' && p ? p : null))
    .filter((p): p is string => p !== null);
  return parts.length ? parts.join(' ') : (asString(t.contract_symbol) ?? asString(t.symbol) ?? 'trade');
}

/** Join the plan's trades to their validator dispositions. Tolerant of a
 *  missing plan/validation; returns [] rather than throwing. */
export function extractTrades(block: Record<string, unknown> | null): ExtractedTrade[] {
  if (!block) return [];
  const plan = asRecord(block.plan);
  const trades = asArray(plan?.trades);
  const validation = asRecord(block.validation) ?? {};
  const dispById = new Map<string, Record<string, unknown>>();
  for (const d of asArray(validation.dispositions)) {
    const rec = asRecord(d);
    const id = rec ? asString(rec.trade_id) : null;
    if (rec && id) dispById.set(id, rec);
  }
  const out: ExtractedTrade[] = [];
  for (const t of trades) {
    const rec = asRecord(t);
    if (!rec) continue;
    const id = asString(rec.trade_id);
    const disp = id ? dispById.get(id) : undefined;
    const violations = asArray(disp?.violations);
    const codes: string[] = [];
    const messages: string[] = [];
    for (const v of violations) {
      const vr = asRecord(v);
      if (!vr) continue;
      const code = asString(vr.code);
      if (code) codes.push(code);
      const msg = asString(vr.message);
      if (msg) messages.push(msg);
    }
    out.push({
      tradeId: id,
      desc: tradeDesc(rec),
      quantity: typeof rec.quantity === 'number' ? rec.quantity : null,
      status: disp ? asString(disp.status) : null,
      codes,
      messages,
    });
  }
  return out;
}

/** Accepted dispositions are what WOULD have been placed in live mode — shadow
 *  mode submits nothing, so this is the closest thing to an "executions" list. */
export function wouldExecute(trades: ExtractedTrade[]): ExtractedTrade[] {
  return trades.filter((t) => t.status === 'accept');
}

/**
 * Reconstruct a plain-language TLDR from the same source fields shadow.py's
 * `telegram_line` uses (the persisted TLDR text itself is send-time only). Never
 * throws; an artifact with no shadow block yields a single neutral line.
 */
export function runTldr(artifact: unknown): string[] {
  const block = shadowBlock(artifact);
  if (!block) {
    const a = asRecord(artifact);
    const status = a ? asString(a.status) : null;
    return status ? [`Run recorded — status ${status}.`] : ['Run recorded.'];
  }
  const intake = asRecord(block.intake) ?? {};
  if (asString(intake.status) === 'abort') {
    const lines = ['No trades today — the strategy pass aborted; recorded as a no-trade day.', 'Nothing submitted (shadow mode).'];
    return lines;
  }
  const trades = extractTrades(block);
  if (trades.length === 0) {
    return ['No trades proposed.', 'Nothing submitted (shadow mode).'];
  }
  const lines = ['Nothing submitted (shadow mode — plans are recorded, never placed).'];
  for (const t of trades) {
    if (t.status === 'accept') {
      lines.push(`Would trade (accepted — recorded only): ${t.desc}${t.quantity !== null ? ` ×${t.quantity}` : ''}.`);
    } else {
      const reason = t.messages[0] ?? t.codes[0] ?? 'rejected by the risk validator';
      lines.push(`Blocked: ${t.desc}${t.quantity !== null ? ` ×${t.quantity}` : ''} — ${reason}.`);
    }
  }
  return lines;
}

// ---------------------------------------------------------------------------
// EOD shadow-eval (eod_shadow_record.py:222 merges `eod_artifact["eod_shadow_eval"]
// = ev.block`; the block is built by eod_shadow.py `evaluate` ~line 122). EOD runs
// carry this block INSTEAD of `shadow_intended` (which is the MORNING decision).
//
//   eod_shadow_eval (eod_shadow.py:122-134): { schema_version, generated_at,
//     execution_mode, submitted, evaluated_morning_run_id, as_of,
//     status:"ok"|"nothing_to_evaluate", total_pnl, marked_count, excluded:{...},
//     legs:[{ trade_id, underlying, contract_symbol, strategy, instrument,
//       direction, quantity, entry, mark, pnl, mark_source_ts }] }
//
// The TLDR is NOT persisted — it is assembled at Telegram-send time by
// eod_shadow.py `telegram_line` (~line 157). `eodTldr` below reconstructs the same
// plain-language output state from the same source fields (status, marked_count,
// total_pnl, excluded), mirroring that builder's logic.
// ---------------------------------------------------------------------------

/** The eod_shadow_eval block off an artifact, or null. */
export function eodBlock(artifact: unknown): Record<string, unknown> | null {
  const a = asRecord(artifact);
  return a ? asRecord(a.eod_shadow_eval) : null;
}

export interface EodLeg {
  /** Plain-language label mirroring eod_shadow.py `_leg_label`
   *  (`{underlying} {CC|CSP|PP|strategy|shares}`). */
  label: string;
  pnl: number | null;
  direction: string | null;
  quantity: number | null;
  entry: number | null;
  mark: number | null;
}

/** Strategy tag, mirroring eod_shadow.py `_STRATEGY_TAG` / `_leg_label`. */
function eodLegLabel(leg: Record<string, unknown>): string {
  const underlying = asString(leg.underlying) ?? '?';
  if (leg.instrument === 'equity') return `${underlying} shares`;
  const strategy = asString(leg.strategy);
  const tag =
    strategy === 'covered_call' ? 'CC'
    : strategy === 'cash_secured_put' ? 'CSP'
    : strategy === 'protective_put' ? 'PP'
    : (strategy ?? '?');
  return `${underlying} ${tag}`;
}

/** The marked legs the EOD eval carries. Tolerant of a missing/!ok block. */
export function eodLegs(block: Record<string, unknown> | null): EodLeg[] {
  if (!block) return [];
  const num = (v: unknown): number | null => (typeof v === 'number' && !Number.isNaN(v) ? v : null);
  const out: EodLeg[] = [];
  for (const l of asArray(block.legs)) {
    const rec = asRecord(l);
    if (!rec) continue;
    out.push({
      label: eodLegLabel(rec),
      pnl: num(rec.pnl),
      direction: asString(rec.direction),
      quantity: num(rec.quantity),
      entry: num(rec.entry),
      mark: num(rec.mark),
    });
  }
  return out;
}

/** Signed dollar amount with two fixed decimals, mirroring eod_shadow.py
 *  `_signed` (`{'+'|'-'}${abs:.2f}`). */
function signedMoney(n: number): string {
  return `${n >= 0 ? '+' : '-'}$${Math.abs(n).toFixed(2)}`;
}

/**
 * Reconstruct the EOD TLDR from the same fields eod_shadow.py `telegram_line`
 * uses (status, marked_count, total_pnl, excluded). Never throws; a !ok block
 * yields the "no morning plan to evaluate" lines.
 */
export function eodTldr(block: Record<string, unknown> | null): string[] {
  if (!block) return [];
  if (block.status !== 'ok') {
    return [
      'No morning plan to evaluate — nothing to mark.',
      'Nothing submitted (shadow mode).',
      'No action needed.',
    ];
  }
  const marked = typeof block.marked_count === 'number' ? block.marked_count : 0;
  const total = typeof block.total_pnl === 'number' ? block.total_pnl : 0;
  const excluded = asRecord(block.excluded) ?? {};
  let notMarked = 0;
  for (const v of Object.values(excluded)) if (typeof v === 'number') notMarked += v;
  const gain = total >= 0 ? 'made' : 'lost';
  let valued = `${marked} leg(s) valued`;
  if (notMarked) valued += `, ${notMarked} could not be valued`;
  return [
    `The morning's intended plan would have ${gain} ${signedMoney(total)} by the close (${valued}).`,
    'Nothing submitted (shadow mode — evaluation only).',
    'No action needed.',
  ];
}

/** The persisted decision narrative: the plan summary, or the abort reason. */
export function runNarrative(artifact: unknown): string | null {
  const block = shadowBlock(artifact);
  if (!block) return null;
  const plan = asRecord(block.plan);
  const planSummary = plan ? asString(plan.summary) : null;
  if (planSummary) return planSummary;
  const intake = asRecord(block.intake);
  return intake ? asString(intake.reason) : null;
}

/** Escalations the artifact may record (best-effort; not always present). The
 *  agent surfaces corporate-action / escalation notices on the index row's
 *  notifications or a top-level field — we read any array we recognize. */
export function extractEscalations(artifact: unknown): unknown[] {
  const a = asRecord(artifact);
  if (!a) return [];
  const candidates = [a.escalations, a.escalation, asRecord(a.market_context)?.escalations];
  for (const c of candidates) if (Array.isArray(c) && c.length) return c;
  return [];
}

function fmtNum(n: number | null, opts?: { money?: boolean; pct?: boolean }): string {
  if (n === null) return '—';
  if (opts?.pct) return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  if (opts?.money) return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return String(n);
}

// ---------------------------------------------------------------------------
// Detail panel.
// ---------------------------------------------------------------------------

function RunDetail({ runId, onClose }: { runId: string; onClose: () => void }) {
  const { data, isLoading, isError, error } = useRun(runId);

  return (
    <section className="run-detail" aria-label={`Run ${runId}`}>
      <header className="run-detail-header">
        <h2 className="run-detail-title">{runId}</h2>
        <button type="button" className="btn btn-close" onClick={onClose} aria-label="Close run detail">
          Back to timeline
        </button>
      </header>

      {isLoading && <p className="run-detail-loading">Loading run…</p>}
      {isError && (
        <p className="run-detail-error error" role="alert">
          Failed to load run: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      )}

      {data && <RunDetailBody index={data.index} artifact={data.artifact} />}
    </section>
  );
}

function RunDetailBody({ index, artifact }: { index: RunIndexRow; artifact: unknown }) {
  const pill = statusPill(index.status);
  const { started, completed } = rowTimestamps(index);
  const counts = summaryCounts(index);

  const hasArtifact = asRecord(artifact) !== null;
  const block = shadowBlock(artifact);
  const trades = extractTrades(block);
  const executions = wouldExecute(trades);
  const escalations = extractEscalations(artifact);
  const narrative = runNarrative(artifact);
  // EOD runs carry `eod_shadow_eval` instead of the morning `shadow_intended`.
  const eod = eodBlock(artifact);
  const eodTldrLines = eodTldr(eod);
  const eodLegRows = eodLegs(eod);
  // The morning TLDR is the right surface for morning runs; for an EOD run the
  // EOD section below carries the narrative, so suppress the morning TLDR's
  // generic "Run recorded" fallback when an EOD block is present.
  const tldr = eod ? [] : runTldr(artifact);

  return (
    <div className="run-detail-body">
      <dl className="run-detail-meta">
        <div className="meta-row">
          <dt>status</dt>
          <dd><span className={pill.className} data-status={pill.label}>{pill.label}</span></dd>
        </div>
        {asString(index.routine) && (
          <div className="meta-row"><dt>routine</dt><dd>{String(index.routine)}</dd></div>
        )}
        {started && <div className="meta-row"><dt>started</dt><dd>{started}</dd></div>}
        {completed && <div className="meta-row"><dt>completed</dt><dd>{completed}</dd></div>}
        {counts.positions !== null && (
          <div className="meta-row"><dt>positions</dt><dd>{counts.positions}</dd></div>
        )}
        {counts.pnlAmount !== null && (
          <div className="meta-row">
            <dt>pnl</dt>
            <dd>{fmtNum(counts.pnlAmount, { money: true })}{counts.pnlPct !== null ? ` (${fmtNum(counts.pnlPct, { pct: true })})` : ''}</dd>
          </div>
        )}
      </dl>

      {!hasArtifact && (
        <p className="run-detail-no-artifact" role="note">
          No artifact available for this run — showing the index row only.
        </p>
      )}

      {/* TLDR — always renderable (reconstructed from the same source fields the
          Telegram report uses; the persisted TLDR text is send-time only). For an
          EOD run this is empty and the EOD section below carries the TLDR. */}
      {hasArtifact && tldr.length > 0 && (
        <section className="run-tldr" aria-label="TLDR">
          <h3 className="section-title">TLDR</h3>
          <ul className="run-tldr-list">
            {tldr.map((line, i) => (
              <li key={i} className="run-tldr-line">{line}</li>
            ))}
          </ul>
        </section>
      )}

      {/* EOD shadow-eval — the morning plan's accepted legs marked to market at
          the close (eod_shadow_eval block). Mirrors the EOD telegram_line: a
          reconstructed TLDR plus the per-leg P&L rows the block carries. */}
      {eod && (
        <section className="run-eod" aria-label="EOD shadow evaluation">
          <h3 className="section-title">EOD shadow evaluation</h3>
          <ul className="run-eod-tldr-list">
            {eodTldrLines.map((line, i) => (
              <li key={i} className="run-eod-tldr-line">{line}</li>
            ))}
          </ul>
          {eodLegRows.length > 0 && (
            <ul className="run-eod-legs">
              {eodLegRows.map((leg, i) => (
                <li key={i} className="run-eod-leg">
                  <span className="eod-leg-label">{leg.label}</span>
                  {leg.pnl !== null && (
                    <span className="eod-leg-pnl">{signedMoney(leg.pnl)}</span>
                  )}
                  {leg.entry !== null && leg.mark !== null && (
                    <span className="eod-leg-marks">
                      entry {fmtNum(leg.entry, { money: true })} → mark {fmtNum(leg.mark, { money: true })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {narrative && (
        <section className="run-narrative" aria-label="Decision narrative">
          <h3 className="section-title">Narrative</h3>
          <p className="run-narrative-text">{narrative}</p>
        </section>
      )}

      {trades.length > 0 && (
        <section className="run-plan" aria-label="Proposed plan">
          <h3 className="section-title">Proposed plan</h3>
          <ul className="run-plan-list">
            {trades.map((t, i) => {
              const tPill = statusPill(t.status ?? 'unknown');
              return (
                <li key={t.tradeId ?? i} className="run-plan-trade" data-trade-id={t.tradeId ?? ''}>
                  <span className="trade-desc">{t.desc}</span>
                  {t.quantity !== null && <span className="trade-qty">×{t.quantity}</span>}
                  <span className={tPill.className} data-status={tPill.label}>{tPill.label}</span>
                  {t.codes.length > 0 && (
                    <ul className="trade-verdicts">
                      {t.codes.map((code, ci) => (
                        <li key={code + ci} className="trade-verdict">
                          {/* ReasonCode shown AS-IS, plus the plain-language line
                              when the artifact carries it (violation.message). */}
                          <code className="reason-code">{code}</code>
                          {t.messages[ci] && <span className="reason-plain"> — {t.messages[ci]}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {executions.length > 0 && (
        <section className="run-executions" aria-label="Would-execute">
          <h3 className="section-title">Would execute (live mode)</h3>
          <p className="run-executions-note">Shadow mode submits nothing — these accepted trades would have been placed in live mode.</p>
          <ul className="run-executions-list">
            {executions.map((t, i) => (
              <li key={t.tradeId ?? i} className="run-execution">
                {t.desc}{t.quantity !== null ? ` ×${t.quantity}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      {escalations.length > 0 && (
        <section className="run-escalations" aria-label="Escalations">
          <h3 className="section-title">Escalations</h3>
          <ul className="run-escalations-list">
            {escalations.map((e, i) => (
              <li key={i} className="run-escalation">
                <pre className="escalation-raw">{safeStringify(e)}</pre>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Raw-JSON fallback for anything unrecognized — always available so an
          unexpected artifact shape is inspectable rather than silently dropped. */}
      {hasArtifact && (
        <details className="run-raw">
          <summary>Raw artifact JSON</summary>
          <pre className="run-raw-json">{safeStringify(artifact)}</pre>
        </details>
      )}
    </div>
  );
}

/** JSON.stringify that never throws (circular refs etc. fall back to String). */
export function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

// ---------------------------------------------------------------------------
// Timeline row.
// ---------------------------------------------------------------------------

function TimelineRow({ row, onOpen }: { row: RunIndexRow; onOpen: (id: string) => void }) {
  const pill = statusPill(row.status);
  const { started, completed } = rowTimestamps(row);
  const counts = summaryCounts(row);

  return (
    <li className="runs-timeline-row" data-run-id={row.run_id} data-status={pill.label}>
      <button type="button" className="runs-timeline-button" onClick={() => onOpen(row.run_id)} aria-label={`Open run ${row.run_id}`}>
        <span className="run-id">{row.run_id}</span>
        {asString(row.routine) && <span className="run-routine">{String(row.routine)}</span>}
        <span className={pill.className} data-status={pill.label}>{pill.label}</span>
        <span className="run-times">
          {started ?? completed ?? '—'}
          {completed && started && completed !== started ? ` → ${completed}` : ''}
        </span>
        <span className="run-counts">
          {counts.positions !== null && <span className="count-positions">{counts.positions} pos</span>}
          {counts.accepted !== null && <span className="count-accepted">{counts.accepted} acc</span>}
          {counts.rejected !== null && <span className="count-rejected">{counts.rejected} rej</span>}
          {counts.pnlAmount !== null && (
            <span className="count-pnl">{fmtNum(counts.pnlAmount, { money: true })}</span>
          )}
        </span>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// The tab.
// ---------------------------------------------------------------------------

export default function RunsTab() {
  const { data, isLoading, isError, error } = useRuns(50);
  const [openId, setOpenId] = useState<string | null>(null);

  if (isLoading) return <p className="runs-loading">Loading runs…</p>;
  if (isError) {
    return (
      <p className="runs-error error" role="alert">
        Failed to load runs: {error instanceof Error ? error.message : 'unknown error'}
      </p>
    );
  }

  const runs = data?.runs ?? [];

  if (openId !== null) {
    return <RunDetail runId={openId} onClose={() => setOpenId(null)} />;
  }

  return (
    <section className="runs-tab" aria-label="Runs">
      {runs.length === 0 ? (
        <p className="runs-empty">No runs recorded yet.</p>
      ) : (
        <ol className="runs-timeline">
          {runs.map((row) => (
            <TimelineRow key={row.run_id} row={row} onOpen={setOpenId} />
          ))}
        </ol>
      )}
    </section>
  );
}
